
import { motion } from "framer-motion";
import { PlotlyChart } from "./PlotlyChart";
import type { AnalyticsResult, MessageType } from "@/services/api";
import "./ChatMessage.css";

export interface ChatMessageProps {
  type: MessageType;
  content: string;
  result?: AnalyticsResult | null;
  isLoading?: boolean;
  clarification_questions?: string[];
  onClarificationAnswer?: (answer: string) => void;
  timestamp?: number;
}

export function ChatMessage({ type, content, result, isLoading, clarification_questions, onClarificationAnswer, timestamp }: ChatMessageProps) {
  const isUser = type === "user";

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className={`chat-message${isUser ? " chat-message--user" : ""}`}
    >
      {/* Avatar for assistant only */}
      {!isUser && <div className="message-avatar">⚡</div>}

      {/* Message bubble */}
      <div className="message-content">
        {/* Message text */}
        <div className={`message-bubble${isUser ? " message-bubble--user" : ""}`}>
          {isLoading ? (
            <div className="message-loading">
              <div className="loading-dot" />
              <span className="loading-text">Analyzing your query…</span>
            </div>
          ) : (
            content
          )}
        </div>

        {/* Clarification questions */}
        {clarification_questions && clarification_questions.length > 0 && (
          <div className="clarification-block">
            <ul className="clarification-questions">
              {clarification_questions.map((q, i) => (
                <li key={i} className="clarification-question">{q}</li>
              ))}
            </ul>
            <div className="clarification-chips">
              {clarification_questions.map((q, i) => (
                <button
                  key={i}
                  className="clarification-chip"
                  onClick={() => onClarificationAnswer?.(q)}
                >
                  {q}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Results section */}
        {result && (
          <div className={`message-results${isUser ? " message-results--user" : ""}`}>
            {/* Metrics */}
            {result.key_metrics && result.key_metrics.length > 0 && (
              <div className="metrics-row">
                {result.key_metrics.map((m, i) => {
                  const trendClass = m.trend === "up"
                    ? "metric-chip-delta--up"
                    : m.trend === "down"
                    ? "metric-chip-delta--down"
                    : "";
                  const trendIcon = m.trend === "up" ? "▲" : m.trend === "down" ? "▼" : "—";

                  return (
                    <div key={i} className="metric-chip">
                      <div className="metric-chip-name">{m.name}</div>
                      <div className="metric-chip-value">{m.value}</div>
                      {m.delta && (
                        <div className={`metric-chip-delta ${trendClass}`}>
                          <span>{trendIcon}</span>
                          <span>{m.delta}</span>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            {/* Charts */}
            {result.charts && result.charts.length > 0 && (
              <div className="charts-grid">
                {result.charts.map((chart) => (
                  <div key={chart.id} className="chart-item">
                    <PlotlyChart chart={chart} />
                  </div>
                ))}
              </div>
            )}

            {/* Insights */}
            {result.insights && result.insights.length > 0 && (
              <div className="insights-list">
                {result.insights.slice(0, 3).map((insight, i) => (
                  <div key={i} className={`insight-item insight-item--${insight.impact}`}>
                    <div className="insight-item-title">💡 {insight.title}</div>
                    <div className="insight-item-desc">{insight.description}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Timestamp */}
        {timestamp && (
          <div className="message-timestamp">
            {new Date(timestamp).toLocaleTimeString([], {
              hour: "2-digit",
              minute: "2-digit",
            })}
          </div>
        )}
      </div>
    </motion.div>
  );
}
